// src/components/Documents/DocumentUpload.js
import React, { useState } from 'react';
import { Upload, X, FileText, Image, File, CheckCircle, AlertCircle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import toast from 'react-hot-toast';

const DocumentUpload = ({ onUploadComplete, onClose }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const { user } = useAuth();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp']
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(rejection => {
          const error = rejection.errors[0];
          if (error.code === 'file-too-large') {
            toast.error(`${rejection.file.name} is too large. Maximum size is 50MB.`);
          } else if (error.code === 'file-invalid-type') {
            toast.error(`${rejection.file.name} is not a supported file type.`);
          }
        });
      }

      if (acceptedFiles.length > 0) {
        const newFiles = acceptedFiles.map(file => ({
          file,
          id: Math.random().toString(36).substr(2, 9),
          status: 'pending',
          progress: 0
        }));
        setFiles(prev => [...prev, ...newFiles]);
      }
    }
  });

  const removeFile = (fileId) => {
    setFiles(files => files.filter(f => f.id !== fileId));
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (type.includes('word')) return <FileText className="w-8 h-8 text-blue-500" />;
    if (type.includes('excel') || type.includes('sheet')) return <FileText className="w-8 h-8 text-green-500" />;
    if (type.includes('powerpoint') || type.includes('presentation')) return <FileText className="w-8 h-8 text-orange-500" />;
    if (type.includes('image')) return <Image className="w-8 h-8 text-purple-500" />;
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    const results = { success: 0, failed: 0 };

    try {
      for (const fileItem of files) {
        if (fileItem.status === 'completed') continue;

        try {
          // Update file status to uploading
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f
          ));

          // Simulate upload progress (you can replace this with actual progress tracking)
          const progressInterval = setInterval(() => {
            setFiles(prev => prev.map(f => {
              if (f.id === fileItem.id && f.progress < 90) {
                return { ...f, progress: f.progress + 10 };
              }
              return f;
            }));
          }, 200);

          // Upload file to storage
          const uploadedFile = await documentService.uploadFile(fileItem.file, user.uid);
          
          // Create document record
          const documentData = {
            name: fileItem.file.name,
            type: fileItem.file.type,
            size: fileItem.file.size,
            url: uploadedFile.url,
            path: uploadedFile.path,
            tags: [],
            description: ''
          };
          
          await documentService.createDocument(documentData, user.uid);

          clearInterval(progressInterval);

          // Mark as completed
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id ? { ...f, status: 'completed', progress: 100 } : f
          ));

          results.success++;
        } catch (error) {
          console.error(`Error uploading ${fileItem.file.name}:`, error);
          
          // Mark as failed
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id ? { ...f, status: 'failed', error: error.message } : f
          ));

          results.failed++;
        }
      }

      // Show results
      if (results.success > 0) {
        toast.success(`${results.success} file(s) uploaded successfully!`);
      }
      if (results.failed > 0) {
        toast.error(`${results.failed} file(s) failed to upload`);
      }

      // Notify parent component
      if (onUploadComplete) {
        onUploadComplete();
      }

    } finally {
      setUploading(false);
    }
  };

  const clearCompleted = () => {
    setFiles(files => files.filter(f => f.status !== 'completed'));
  };

  const hasCompletedFiles = files.some(f => f.status === 'completed');
  const hasPendingFiles = files.some(f => f.status === 'pending');

  return (
    <div className="bg-white rounded-lg shadow-xl border-0 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
          <p className="text-sm text-gray-600">Drag and drop files or click to select</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Upload Zone */}
      <div className="p-6">
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-indigo-500 bg-indigo-50' 
              : 'border-gray-300 hover:border-gray-400'
            }
            ${uploading ? 'opacity-50 pointer-events-none' : ''}
          `}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium text-gray-600 mb-2">
            {isDragActive ? 'Drop files here...' : 'Upload Documents'}
          </p>
          <p className="text-sm text-gray-500 mb-2">
            Drag and drop files here, or click to select files
          </p>
          <p className="text-xs text-gray-400">
            Supports PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, and image files (Max 50MB each)
          </p>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Selected Files ({files.length})
              </h3>
              <div className="flex space-x-2">
                {hasCompletedFiles && (
                  <button
                    onClick={clearCompleted}
                    className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Clear Completed
                  </button>
                )}
                {hasPendingFiles && (
                  <button
                    onClick={uploadFiles}
                    disabled={uploading}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {uploading ? 'Uploading...' : 'Upload All'}
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {files.map((fileItem) => (
                <div key={fileItem.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3 flex-1">
                    {getFileIcon(fileItem.file.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {fileItem.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(fileItem.file.size)}
                      </p>
                      
                      {/* Progress Bar */}
                      {fileItem.status === 'uploading' && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300" 
                              style={{ width: `${fileItem.progress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{fileItem.progress}%</p>
                        </div>
                      )}

                      {/* Error Message */}
                      {fileItem.status === 'failed' && (
                        <p className="text-xs text-red-600 mt-1">{fileItem.error}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Status Icon */}
                    {fileItem.status === 'completed' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {fileItem.status === 'failed' && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                    {fileItem.status === 'uploading' && (
                      <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    )}

                    {/* Remove Button */}
                    {fileItem.status !== 'uploading' && (
                      <button
                        onClick={() => removeFile(fileItem.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Upload Tips:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, Images</li>
            <li>• Maximum file size: 50MB per file</li>
            <li>• You can upload multiple files at once</li>
            <li>• Files are automatically organized by upload date</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;